import React, { useContext, useState } from "react";

import { Progress, Button, Typography, Divider } from "antd";
import { AppContext } from "App";
import arrowCounterClockwise from "assets/images/arrow-counter-clockwise.svg";
import ButtonLoading from "components/basic/Spinner/ButtonLoading";

import { Answer, AnswersArr, RESULT_TYPES } from "./RequirementsChecker";

interface StepConfig {
    stepTitle: string;
    stepDescription: string;
    question: string;
    questionHelper?: string;
    answerOptions: string[];
    multiSelect?: boolean;
}

const steps: StepConfig[] = [
    {
        stepTitle: "Self Assessment Status",
        stepDescription: "Let's start by checking if you currently file a Self Assessment",
        question: "Do you currently submit a Self Assessment tax return?",
        answerOptions: ["Yes", "No"],
    },
    {
        stepTitle: "Business Type",
        stepDescription: "We need to know about your business activities",
        question: "Were you a sole trader or/and landlord in the 2024/2025 tax year? (Select all that apply)",
        answerOptions: [
            "Sole trader",
            "Landlord (UK property)",
            "Landlord (Foreign property)",
            "None",
        ],
        multiSelect: true,
    },
    {
        stepTitle: "Income Level",
        stepDescription: "Your income level determines when MTD requirements apply",
        question: "What was your total gross income from self-employment and/or property rental in the 2024/2025 tax year?",
        questionHelper: "Your gross income is the total money you earn before deducting any expenses or taxes.",
        answerOptions: [
            "Less than £10,000",
            "£10,000 to £19,999",
            "£20,000 to £29,999",
            "£30,000 to £49,999",
            "£50,000 or more",
        ],
    },
];

interface RequirementsCheckerQuestionnaireProps {
    onComplete: (_resultType: string) => void;
    current: number;
    selected: string[];
    setAnswers: React.Dispatch<React.SetStateAction<AnswersArr>>;
    answers: AnswersArr;
    setCurrent: React.Dispatch<React.SetStateAction<number>>;
    setSelected: React.Dispatch<React.SetStateAction<string[]>>;
    handleBack: () => void;
}

const RequirementsCheckerQuestionnaire: React.FC<RequirementsCheckerQuestionnaireProps> = ({
    onComplete,
    current,
    selected,
    setAnswers,
    answers,
    setCurrent,
    setSelected,
    handleBack,
}) => {
    const { isMobile } = useContext(AppContext);

    const totalSteps = steps.length;
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isLoadingNext, setIsLoadingNext] = useState(false);

    const {
        stepTitle,
        stepDescription,
        question,
        questionHelper,
        answerOptions,
        multiSelect,
    } = steps[current];

    const handleNext = (selected: string[]) => {
        const newAnswer: Answer = {
            question,
            answer: multiSelect ? selected : selected[0],
        };
        const updatedAnswers = answers.some(a => a.question === question)
            ? answers.map(a => (a.question === question ? newAnswer : a))
            : [...answers, newAnswer];
        setAnswers(updatedAnswers);

        if (current === 1 && selected.length === 1 && selected[0] === "None") {
            onComplete(RESULT_TYPES.NOT_REGISTERED);
            return;
        }

        if (current === 2) {
            let resultType: string;
            switch (selected[0]) {
            case "Less than £10,000":
                resultType = RESULT_TYPES.NOT_NEED; break;
            case "£10,000 to £19,999":
                resultType = RESULT_TYPES.TOO_EARLY; break;
            case "£20,000 to £29,999":
                resultType = RESULT_TYPES.MTD_2028; break;
            case "£30,000 to £49,999":
                resultType = RESULT_TYPES.MTD_2027; break;
            case "£50,000 or more":
                resultType = RESULT_TYPES.MTD_2026; break;
            default:
                resultType = RESULT_TYPES.NOT_NEED;
            }
            onComplete(resultType);
            return;
        }

        if (current < totalSteps - 1) {
            setCurrent(curr => curr + 1);
            setSelected([]);
        }
    };

    const handleNextClick = () => {
        if (selected.length === 0 || isLoadingNext) return;

        setIsLoadingNext(true);
        setTimeout(() => {
            handleNext(selected);
            setIsLoadingNext(false);
        }, 600);
    };

    const handleOptionClick = (option: string) => {
        if (isTransitioning) return;

        if (!multiSelect) {
            setSelected([option]);

            const newEntry: Answer = { question, answer: option };

            if (current === 0) {
                setIsTransitioning(true);
                const updatedAnswers = answers.some(a => a.question === question)
                    ? answers.map(a => (a.question === question ? newEntry : a))
                    : [...answers, newEntry];
                setAnswers(updatedAnswers);
                setTimeout(() => {
                    if (option === "No") {
                        onComplete(RESULT_TYPES.NOT_RELEVANT);
                    } else {
                        setCurrent(1);
                        setSelected([]);
                    }
                    setIsTransitioning(false);
                }, 600);
                return;
            }

            setIsTransitioning(true);
            setTimeout(() => {
                handleNext([option]);
                setIsTransitioning(false);
            }, 600);
            return;
        }

        const isNone = option === "None";
        let updatedSelection: string[];

        if (isNone) {
            updatedSelection = [option];
        } else {
            const withoutNone = selected.filter(o => o !== "None");
            const alreadySelected = withoutNone.includes(option);
            updatedSelection = alreadySelected
                ? withoutNone.filter(o => o !== option)
                : [...withoutNone, option];
        }

        setSelected(updatedSelection);

        const newAnswer: Answer = { question, answer: updatedSelection };
        const updatedAnswers = answers.some(a => a.question === question)
            ? answers.map(a => (a.question === question ? newAnswer : a))
            : [...answers, newAnswer];

        setAnswers(updatedAnswers);
    };

    const handleClear = () => {
        setCurrent(0);
        setSelected([]);
        setAnswers([]);
    };

    return (<>
        <div className="requirements-checker__questionnaire">
            <div className="requirements-checker__questionnaire__progress">
                <div className="requirements-checker__questionnaire__progress__steps">
                    <div className="requirements-checker__questionnaire__progress__steps__current">{`Step ${current + 1} of ${totalSteps}`}</div>
                    <Button type="text"
                        icon={<img src={arrowCounterClockwise} alt="reset" width={20} style={{ display: "block" }}/>}
                        onClick={handleClear}
                        style={{ display: "flex" }}
                    >
                        Clear Progress
                    </Button>
                </div>
                <div style={{ width: "100%" }}>
                    <Progress
                        percent={Math.round(((isTransitioning || isLoadingNext ? current + 1 : current) / totalSteps) * 100)}
                        showInfo={false}
                        strokeColor="#2EBB49"
                    />
                </div>
            </div>

            <div className="requirements-checker__questionnaire__content">
                <Typography className="requirements-checker__questionnaire__content__title">{stepTitle}</Typography>
                <Typography className="requirements-checker__questionnaire__content__description">{stepDescription}</Typography>
                <Typography className="requirements-checker__questionnaire__content__question">{question}</Typography>
                {questionHelper && (
                    <Typography className="requirements-checker__questionnaire__content__question-helper">{questionHelper}</Typography>
                )}
                <div className="requirements-checker__questionnaire__options">
                    {answerOptions.map(option => (
                        <div
                            key={option}
                            onClick={() => handleOptionClick(option)}
                            className={`requirements-checker__questionnaire__options__option${selected.includes(option) ? "__selected" : ""}`}
                            style={{ minWidth:  isMobile ? answerOptions.length > 2 ? "calc(100% - 25px)" : "calc(50% - 32px)" : "152px" }}
                        >
                            {option}
                        </div>
                    ))}
                </div>
                {!isMobile && <Divider style={{ marginTop: "70px" }}/>}
                <div className="requirements-checker__questionnaire__buttons">
                    <button
                        className="requirements-checker__questionnaire__buttons__back-button"
                        onClick={handleBack}
                    >
                        <span>
                            Back
                        </span>
                    </button>
                    <button
                        className="requirements-checker__questionnaire__buttons__next-button"
                        onClick={handleNextClick}
                        disabled={selected.length === 0}
                    >
                        <span>
                            {isTransitioning || isLoadingNext ? (
                                <ButtonLoading/>
                            ) : <span>Next</span>}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </>
    );
};

export default RequirementsCheckerQuestionnaire;
